/**
 * ll-product-addons.js — "Product Add-ons" widget (code replacement for the retired Fast Bundle
 * add-on widget). Rendered by snippets/ll-product-addons.liquid on the main product page only.
 *
 * Behaviour:
 * - Single-select (checkbox-styled radios), ONE add-on max; clicking the selected one deselects it.
 * - One labelled dropdown PER product option (Size / TOG…); the chosen combination resolves to a
 *   variant via the JSON embedded per item ({"variants":[{id, price, options:[…]}…]}, available
 *   variants only). If a combination doesn't exist, the other dropdowns snap to the first
 *   available variant matching the changed value.
 * - Color swatches (linked-swatches sibling products): clicking a swatch fetches the sibling
 *   product via /products/<handle>.js and swaps the item's image, title, link, option dropdowns
 *   and price in place. No page reload, no Liquid `all_products` budget spent.
 * - Listens for the main product form's `variant:add` event (dispatched by theme.js on a
 *   successful add, and by the embroidery interceptor on its combined-add path) and then
 *   posts the chosen add-on variant to /cart/add.js as its own line item, followed by a
 *   `cart:refresh` so the mini-cart re-renders with both lines.
 *
 * Checkout pricing is enforced by an automatic "Buy X Get Y" discount configured in the
 * admin — the percentage in this widget is display only. The discount's "Customer gets"
 * list must include every color sibling offered here (see docs/how-to-config.html Part B).
 */
(function () {
  'use strict';

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function formatMoney(cents, format) {
    var tv = (window.themeVariables && window.themeVariables.settings) || {};

    // Plain money format on purpose (no "USD" suffix) — keeps prices on one line.
    format = format || tv.moneyFormat || '${{amount}}';

    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) {
        return '0';
      }

      var parts = (number / 100).toFixed(precision).split('.');
      var units = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? units + decimal + parts[1] : units;
    }

    var match = format.match(placeholderRegex);
    var value;

    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return format.replace(placeholderRegex, value);
  }

  class LLProductAddons extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.percent = Math.min(100, Math.max(0, parseInt(this.dataset.percent || '0', 10) || 0));
      this.freeGiftText = this.dataset.freeGiftText || 'Free gift';
      this._current = null;
      this._busy = false;
      this._productCache = {};

      this.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        var swatch = event.target.closest('[data-addon-swatch]');
        if (swatch) {
          this.onSwatchClick(swatch);
          return;
        }

        // Click-to-deselect: a click on an already-selected control unchecks it, so the
        // customer can opt back out of the add-on (radios don't allow this natively).
        var radio = event.target.closest('[data-addon-radio]');
        if (!radio) return;

        if (radio === this._current) {
          radio.checked = false;
          this._current = null;
        } else {
          this._current = radio;
        }
      });

      this.addEventListener('change', (event) => {
        var target = event.target;
        if (target.matches('[data-addon-option]')) {
          this.resolveVariant(target.closest('[data-addon-item]'), target);
        }
      });

      this._onVariantAdd = this.onMainProductAdded.bind(this);
      document.addEventListener('variant:add', this._onVariantAdd);
    }

    disconnectedCallback() {
      if (this._onVariantAdd) {
        document.removeEventListener('variant:add', this._onVariantAdd);
      }
    }

    /** Available variants ({id, price, options[]}) embedded per item. */
    itemVariants(item) {
      if (!item._llVariants) {
        try {
          var json = item.querySelector('[data-addon-variants]');
          item._llVariants = (json ? JSON.parse(json.textContent) : {}).variants || [];
        } catch (error) {
          item._llVariants = [];
        }
      }
      return item._llVariants;
    }

    /** Match the option dropdowns to a variant; snap other dropdowns when the combo doesn't exist. */
    resolveVariant(item, changedSelect) {
      if (!item) return;

      var variants = this.itemVariants(item);
      if (!variants.length) return;

      var selects = Array.from(item.querySelectorAll('[data-addon-option]')).sort(function (a, b) {
        return (parseInt(a.dataset.optionIndex, 10) || 0) - (parseInt(b.dataset.optionIndex, 10) || 0);
      });

      var wanted = selects.map(function (s) { return s.value; });

      var variant = variants.find(function (v) {
        return v.options.every(function (opt, i) { return wanted[i] == null || opt === wanted[i]; });
      });

      if (!variant && changedSelect) {
        var ci = parseInt(changedSelect.dataset.optionIndex, 10) || 0;
        variant = variants.find(function (v) { return v.options[ci] === changedSelect.value; });
      }

      if (!variant) variant = variants[0];

      selects.forEach(function (select, i) {
        if (variant.options[i] != null && select.value !== variant.options[i]) {
          select.value = variant.options[i];
        }
      });

      var radio = item.querySelector('[data-addon-radio]');
      if (radio) radio.value = variant.id;

      this.renderPrice(item, variant.price);
    }

    fetchProduct(handle) {
      if (!this._productCache[handle]) {
        this._productCache[handle] = fetch(rootUrl() + 'products/' + handle + '.js').then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        });
        // Let a transient failure be retried on the next click.
        this._productCache[handle].catch(() => {
          delete this._productCache[handle];
        });
      }
      return this._productCache[handle];
    }

    /** Swap an item to a sibling color product (linked-swatches system: color = own product). */
    async onSwatchClick(swatch) {
      var item = swatch.closest('[data-addon-item]');
      if (!item || swatch.classList.contains('is-selected') || swatch.classList.contains('is-unavailable')) return;

      var handle = swatch.dataset.handle;
      if (!handle) return;

      try {
        var data = await this.fetchProduct(handle);
        var variants = (data.variants || [])
          .filter(function (v) { return v.available; })
          .map(function (v) { return { id: v.id, price: v.price, options: v.options }; });
        if (!variants.length) throw new Error('sold out');

        item.dataset.productHandle = handle;
        item._llVariants = variants;

        var nameEl = item.querySelector('[data-addon-name]');
        if (nameEl) nameEl.textContent = data.title;

        var link = item.querySelector('[data-addon-link]');
        if (link) {
          link.href = rootUrl() + 'products/' + handle;
          link.setAttribute('aria-label', data.title);
        }

        var img = item.querySelector('[data-addon-media] img');
        if (img && data.featured_image) {
          img.src = data.featured_image + (data.featured_image.indexOf('?') === -1 ? '?' : '&') + 'width=240';
          img.removeAttribute('srcset');
          img.alt = data.title;
        }

        this.rebuildOptions(item, data.options || [], variants);

        var radio = item.querySelector('[data-addon-radio]');
        if (radio) radio.value = variants[0].id;

        this.renderPrice(item, variants[0].price);

        item.querySelectorAll('[data-addon-swatch]').forEach(function (s) {
          s.classList.toggle('is-selected', s === swatch);
        });
      } catch (error) {
        // Draft/unpublished (404) or fully sold-out sibling: grey the swatch out, keep selection.
        swatch.classList.add('is-unavailable');
        console.warn('[ll-product-addons] color unavailable:', handle, error);
      }
    }

    /** Rebuild the labelled per-option dropdowns after a color switch. */
    rebuildOptions(item, options, variants) {
      var wrap = item.querySelector('[data-addon-opts]');
      if (!wrap) return;

      wrap.textContent = '';

      // "Default Title"-only products get no dropdowns at all.
      var onlyDefault = options.length === 1 && options[0].values && options[0].values.length === 1 && variants.length === 1;
      if (onlyDefault) return;

      var selected = variants[0].options;

      options.forEach(function (option, index) {
        var values = (option.values || []).filter(function (value) {
          return variants.some(function (v) { return v.options[index] === value; });
        });
        if (!values.length) return;

        var opt = document.createElement('div');
        opt.className = 'll-addons__opt';

        var label = document.createElement('span');
        label.className = 'll-addons__opt-label';
        // Normalize shouty option names ("SIZE" → "Size") but keep short acronyms like "TOG".
        var labelText = option.name || '';
        if (labelText === labelText.toUpperCase() && labelText.length > 3) {
          labelText = labelText.charAt(0) + labelText.slice(1).toLowerCase();
        }
        label.textContent = labelText;
        opt.appendChild(label);

        var select = document.createElement('select');
        select.className = 'll-addons__select';
        select.setAttribute('data-addon-option', '');
        select.dataset.optionIndex = String(index);
        select.setAttribute('aria-label', option.name);

        values.forEach(function (value) {
          var o = document.createElement('option');
          o.value = value;
          o.textContent = value;
          if (selected[index] === value) o.selected = true;
          select.appendChild(o);
        });

        opt.appendChild(select);
        wrap.appendChild(opt);
      });
    }

    renderPrice(item, cents) {
      if (!item) return;

      var priceEl = item.querySelector('[data-addon-price]');
      if (!priceEl) return;

      var discounted = Math.floor((cents * (100 - this.percent)) / 100);

      priceEl.textContent = '';

      if (this.percent >= 100) {
        var free = document.createElement('span');
        free.className = 'll-addons__free';
        free.textContent = this.freeGiftText;
        priceEl.appendChild(free);
      } else {
        var sale = document.createElement('span');
        sale.className = 'll-addons__sale';
        sale.textContent = '+' + formatMoney(discounted);
        priceEl.appendChild(sale);
      }

      if (this.percent > 0) {
        priceEl.appendChild(document.createTextNode(' '));
        var compare = document.createElement('s');
        compare.className = 'll-addons__compare';
        compare.textContent = formatMoney(cents);
        priceEl.appendChild(compare);
      }
    }

    async onMainProductAdded(event) {
      var form = event.target;
      if (!form || !form.getAttribute || form.getAttribute('id') !== this.dataset.formId) return;

      var radio = this._current && this._current.checked ? this._current : this.querySelector('[data-addon-radio]:checked');
      if (!radio) return;

      var variantId = parseInt(radio.value, 10);
      if (!variantId || this._busy) return;

      this._busy = true;

      try {
        var response = await fetch(rootUrl() + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties: { _llAddon: '1' } }]
          })
        });

        if (response.ok) {
          radio.checked = false;
          this._current = null;
          // The mini-cart just rendered with only the main product; refresh it so the
          // add-on line shows up too (same pattern as the embroidery combined add).
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } else {
          console.error('[ll-product-addons] cart/add.js failed:', await response.text());
        }
      } catch (error) {
        console.error('[ll-product-addons] failed to add add-on to cart:', error);
      } finally {
        this._busy = false;
      }
    }
  }

  if (!window.customElements.get('ll-product-addons')) {
    window.customElements.define('ll-product-addons', LLProductAddons);
  }
})();
